// web/src/pages/email/InboxPage.tsx
//
// Task 18 — Shared inbound-email inbox (`/api/email/inbox`), gated
// `email-inbox:read`. List + filter chips (ListPage/DataGrid, mirroring
// SentEmailPage) with a reading pane on row-select (sanitised HTML body,
// reusing EmailThreadTab's "Show external content" toggle) and triage
// actions (read/unread, spam/not-spam, re-thread, delete, bulk).
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { Paperclip } from "lucide-react";
import {
  emailApi,
  type EntityKind,
  type InboxListItem,
  type InboxMessageDetail,
} from "@/lib/email";
import { ListPage } from "@/archetypes/ListPage";
import { DataGrid, type DataGridColumn } from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/empty-state";
import { sanitizeEmailHtml } from "@/lib/sanitize-email";
import { formatDateTime } from "@/lib/format";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "@/hooks/use-toast";
import { buildNotificationHubConnection } from "@/lib/notifications";
import { getAccessToken } from "@/lib/auth";
import { cn } from "@/lib/utils";

const PAGE_SIZE = 50;

type FilterChipId = "all" | "unassigned" | "low-confidence" | "junk" | "bounces";

const FILTER_CHIP_OPTIONS: ReadonlyArray<{ id: FilterChipId; label: string }> = [
  { id: "all", label: "All" },
  { id: "unassigned", label: "Unassigned" },
  { id: "low-confidence", label: "Low-confidence" },
  { id: "junk", label: "Junk" },
  { id: "bounces", label: "Bounces" },
];

const ENTITY_ROUTE: Record<string, string> = {
  Quote: "/quotes",
  Job: "/jobs",
  Invoice: "/invoices",
  Account: "/accounts",
};

const RETHREAD_ENTITY_TYPES: EntityKind[] = ["Quote", "Job", "Invoice", "Account"];

/** Display file name from a comma-separated attachment blob-key list — mirrors
 * the backend's `EmailAttachmentKey.FileName` (segment after the last '/'). */
function attachmentNames(csv: string | null): string[] {
  if (!csv) return [];
  return csv
    .split(",")
    .map((k) => k.trim())
    .filter(Boolean)
    .map((k) => {
      const slash = k.lastIndexOf("/");
      return slash >= 0 && slash < k.length - 1 ? k.slice(slash + 1) : k;
    });
}

function entityChip(item: InboxListItem) {
  if (!item.matchedEntityType || item.matchedEntityId == null) return null;
  const base = ENTITY_ROUTE[item.matchedEntityType];
  const label = `${item.matchedEntityType} #${item.matchedEntityId}`;
  if (!base) return <Badge variant="outline">{label}</Badge>;
  return (
    <Link to={`${base}/${item.matchedEntityId}`} onClick={(e) => e.stopPropagation()}>
      <Badge variant="outline" className="hover:bg-accent">
        {label}
      </Badge>
    </Link>
  );
}

export function InboxPage() {
  const { has } = usePermissions();
  const canTriage = has("email-inbox:triage");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const [filter, setFilter] = useState<FilterChipId>("all");
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [showRemote, setShowRemote] = useState(false);
  const [rethreadOpen, setRethreadOpen] = useState(false);
  const [rethreadType, setRethreadType] = useState<EntityKind>("Quote");
  const [rethreadId, setRethreadId] = useState("");
  const [spamOpen, setSpamOpen] = useState(false);
  const [spamScope, setSpamScope] = useState<"none" | "sender" | "domain">("none");

  const inboxQuery = useQuery({
    queryKey: ["email-inbox", filter, page, searchText],
    queryFn: () =>
      emailApi.inbox({ filter, q: searchText || undefined, page: page + 1, pageSize: PAGE_SIZE }),
  });

  const messageQuery = useQuery({
    queryKey: ["email-message", selectedId],
    queryFn: () => emailApi.message(selectedId!),
    enabled: selectedId != null,
  });

  // Reset transient reading-pane state whenever the selection changes.
  useEffect(() => {
    setShowRemote(false);
  }, [selectedId]);

  function invalidateInbox() {
    void qc.invalidateQueries({ queryKey: ["email-inbox"] });
  }

  // Live "N new emails" toast — the backend's InboxRealtimeNotifier
  // broadcasts InboxNewMail:{count} to all clients on new inbound mail.
  useEffect(() => {
    const conn = buildNotificationHubConnection(async () => getAccessToken());
    conn.on("InboxNewMail", (p: { count: number }) => {
      const label = `${p.count} new email${p.count === 1 ? "" : "s"}`;
      toast.info(label, { action: { label: "Refresh", onClick: invalidateInbox } });
    });
    conn.start().catch(() => { /* withAutomaticReconnect handles transient failures */ });
    return () => { void conn.stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markRead = useMutation({
    mutationFn: (id: number) => emailApi.markRead(id),
    onSuccess: (_data, id) => {
      invalidateInbox();
      void qc.invalidateQueries({ queryKey: ["email-message", id] });
    },
    onError: () => toast.error("Failed to mark message as read."),
  });

  const markUnread = useMutation({
    mutationFn: (id: number) => emailApi.markUnread(id),
    onSuccess: (_data, id) => {
      invalidateInbox();
      void qc.invalidateQueries({ queryKey: ["email-message", id] });
      toast.success("Marked unread.");
    },
    onError: () => toast.error("Failed to mark message as unread."),
  });

  // Auto-mark-read once per newly opened message (keyed on the message id, so
  // the subsequent refetch with isRead:true does not re-trigger this).
  useEffect(() => {
    if (messageQuery.data && !messageQuery.data.isRead) {
      markRead.mutate(messageQuery.data.emailMessageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageQuery.data?.emailMessageId]);

  const markSpam = useMutation({
    mutationFn: ({ id, scope }: { id: number; scope: "none" | "sender" | "domain" }) =>
      emailApi.markSpam(id, scope),
    onSuccess: () => {
      invalidateInbox();
      toast.success("Message marked as spam.");
      setSpamOpen(false);
    },
    onError: (err: Error) => toast.error(`Failed to mark spam: ${err.message}`),
  });

  const notSpam = useMutation({
    mutationFn: (id: number) => emailApi.notSpam(id),
    onSuccess: () => {
      invalidateInbox();
      toast.success("Message restored from spam.");
    },
    onError: () => toast.error("Failed to unmark spam."),
  });

  const deleteMessage = useMutation({
    mutationFn: (id: number) => emailApi.deleteMessage(id),
    onSuccess: () => {
      invalidateInbox();
      toast.success("Message deleted.");
      setSelectedId(null);
    },
    onError: () => toast.error("Failed to delete message."),
  });

  const rethread = useMutation({
    mutationFn: ({ id, entityType, entityId }: { id: number; entityType: EntityKind; entityId: number }) =>
      emailApi.rethread(id, entityType, entityId),
    onSuccess: () => {
      invalidateInbox();
      toast.success("Message re-threaded.");
      setRethreadOpen(false);
    },
    onError: (err: Error) => toast.error(`Failed to re-thread: ${err.message}`),
  });

  const bulk = useMutation({
    mutationFn: ({ ids, action }: { ids: number[]; action: "read" | "unread" | "mark-spam" | "delete" }) =>
      emailApi.bulk(ids, action),
    onSuccess: () => {
      invalidateInbox();
      toast.success("Bulk action applied.");
      setCheckedIds(new Set());
    },
    onError: (err: Error) => toast.error(`Bulk action failed: ${err.message}`),
  });

  const rows = inboxQuery.data?.items ?? [];

  function toggleChecked(id: number) {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allChecked = rows.length > 0 && rows.every((r) => checkedIds.has(r.emailMessageId));

  const cols: DataGridColumn<InboxListItem>[] = [
    {
      id: "select",
      header: (
        <input
          type="checkbox"
          aria-label="Select all"
          checked={allChecked}
          onChange={(e) => {
            e.stopPropagation();
            setCheckedIds(e.target.checked ? new Set(rows.map((r) => r.emailMessageId)) : new Set());
          }}
        />
      ),
      accessor: (r) => (
        <input
          type="checkbox"
          aria-label={`Select ${r.subject}`}
          checked={checkedIds.has(r.emailMessageId)}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleChecked(r.emailMessageId)}
        />
      ),
      width: "2rem",
    },
    { id: "receivedAt", header: "Received", accessor: (r) => formatDateTime(r.receivedAtUtc) },
    { id: "from", header: "From", accessor: (r) => r.from },
    {
      id: "subject",
      header: "Subject",
      accessor: (r) => (
        <button
          type="button"
          className={cn("text-left underline-offset-2 hover:underline", !r.isRead && "font-semibold")}
          onClick={() => setSelectedId(r.emailMessageId)}
        >
          {r.subject}
        </button>
      ),
    },
    { id: "entity", header: "Matched entity", accessor: (r) => entityChip(r) ?? "—" },
    { id: "snippet", header: "Snippet", accessor: (r) => r.snippet ?? "" },
    {
      id: "attachments",
      header: "",
      accessor: (r) => (r.hasAttachments ? <Paperclip className="size-4 text-muted-foreground" /> : null),
      width: "2rem",
    },
  ];

  const msg: InboxMessageDetail | undefined = messageQuery.data;
  const bodyHtml = msg?.bodyHtml ? sanitizeEmailHtml(msg.bodyHtml, { allowRemoteImages: showRemote }) : "";
  const matchedType = msg
    ? (msg.quoteId != null && "Quote") ||
      (msg.jobId != null && "Job") ||
      (msg.invoiceId != null && "Invoice") ||
      (msg.accountId != null && "Account") ||
      null
    : null;
  const matchedId = msg ? msg.quoteId ?? msg.jobId ?? msg.invoiceId ?? msg.accountId ?? null : null;

  return (
    <>
      <ListPage
        title="Inbox"
        search={{
          value: searchText,
          onChange: (v) => {
            setSearchText(v);
            setPage(0);
          },
          placeholder: "Search inbox…",
        }}
        filterChips={{
          options: FILTER_CHIP_OPTIONS,
          value: filter,
          onChange: (next) => {
            setFilter(next);
            setPage(0);
            setCheckedIds(new Set());
          },
          "aria-label": "Inbox filter",
        }}
      >
        {canTriage && checkedIds.size > 0 && (
          <div className="mb-2 flex items-center gap-2 rounded-md border border-border bg-muted/40 p-2 text-sm">
            <span>{checkedIds.size} selected</span>
            <Button size="sm" variant="outline" onClick={() => bulk.mutate({ ids: [...checkedIds], action: "read" })}>
              Mark read
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulk.mutate({ ids: [...checkedIds], action: "unread" })}>
              Mark unread
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulk.mutate({ ids: [...checkedIds], action: "mark-spam" })}>
              Mark spam
            </Button>
            <Button size="sm" variant="destructive" onClick={() => bulk.mutate({ ids: [...checkedIds], action: "delete" })}>
              Delete
            </Button>
          </div>
        )}

        <div className="flex gap-4">
          <div className="min-w-0 flex-1">
            <DataGrid
              rows={rows}
              columns={cols}
              rowKey={(r) => r.emailMessageId}
              totalRows={inboxQuery.data?.total ?? 0}
              pageIndex={page}
              pageSize={PAGE_SIZE}
              loading={inboxQuery.isLoading}
              emptyHeadline="Inbox zero. Nice."
              onPageChange={setPage}
              onRowClick={(r) => setSelectedId(r.emailMessageId)}
            />
          </div>

          <div className="w-[420px] shrink-0">
            {selectedId == null ? (
              <Card>
                <CardContent className="p-0">
                  <EmptyState headline="Select a message to read it." />
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="space-y-2">
                  {messageQuery.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
                  {messageQuery.isError && (
                    <p role="alert" className="text-sm text-destructive">
                      Failed to load message.
                    </p>
                  )}
                  {msg && (
                    <>
                      <h3 className="text-base font-semibold">{msg.subject}</h3>
                      <div className="space-y-0.5 text-xs text-muted-foreground">
                        <div>From: {msg.fromEmailAddress}</div>
                        {msg.toEmailAddress && <div>To: {msg.toEmailAddress}</div>}
                        {msg.cc && <div>Cc: {msg.cc}</div>}
                        <div>{formatDateTime(msg.receivedAtUtc)}</div>
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            msg.isRead ? markUnread.mutate(msg.emailMessageId) : markRead.mutate(msg.emailMessageId)
                          }
                        >
                          {msg.isRead ? "Mark unread" : "Mark read"}
                        </Button>
                        {matchedType && matchedId != null && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`${ENTITY_ROUTE[matchedType]}/${matchedId}`)}
                          >
                            Open thread
                          </Button>
                        )}
                        {canTriage && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setRethreadOpen(true)}>
                              Re-thread
                            </Button>
                            {msg.junkFlag ? (
                              <Button size="sm" variant="outline" onClick={() => notSpam.mutate(msg.emailMessageId)}>
                                Not spam
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => setSpamOpen(true)}>
                                Mark spam
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => deleteMessage.mutate(msg.emailMessageId)}
                            >
                              Delete
                            </Button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </CardHeader>
                {msg && (
                  <CardContent className="space-y-3 border-t border-border pt-3">
                    <button
                      type="button"
                      className="text-xs underline text-muted-foreground"
                      onClick={() => setShowRemote((v) => !v)}
                    >
                      {showRemote ? "Hide external content" : "Show external content"}
                    </button>
                    <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
                    {attachmentNames(msg.attachmentBlobKeys).length > 0 && (
                      <ul className="flex flex-wrap gap-2">
                        {attachmentNames(msg.attachmentBlobKeys).map((name, i) => (
                          <li key={i}>
                            <a
                              className="rounded border border-border px-2 py-1 text-xs underline"
                              href={`/api/email/messages/${msg.emailMessageId}/attachments/${i}`}
                            >
                              {name}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardContent>
                )}
              </Card>
            )}
          </div>
        </div>
      </ListPage>

      <Dialog open={rethreadOpen} onOpenChange={setRethreadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Re-thread message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="rethread-type">Entity type</Label>
              <Select value={rethreadType} onValueChange={(v) => setRethreadType(v as EntityKind)}>
                <SelectTrigger id="rethread-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RETHREAD_ENTITY_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="rethread-id">Entity id</Label>
              <Input
                id="rethread-id"
                type="number"
                value={rethreadId}
                onChange={(e) => setRethreadId(e.target.value)}
                placeholder="e.g. 123"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={!rethreadId || selectedId == null || rethread.isPending}
              onClick={() =>
                selectedId != null &&
                rethread.mutate({ id: selectedId, entityType: rethreadType, entityId: Number(rethreadId) })
              }
            >
              {rethread.isPending ? "Saving…" : "Save"}
            </Button>
            <Button variant="outline" onClick={() => setRethreadOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={spamOpen} onOpenChange={setSpamOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as spam</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="spam-scope"
                checked={spamScope === "none"}
                onChange={() => setSpamScope("none")}
              />
              Flag this message only
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="spam-scope"
                checked={spamScope === "sender"}
                onChange={() => setSpamScope("sender")}
              />
              Block this sender
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="spam-scope"
                checked={spamScope === "domain"}
                onChange={() => setSpamScope("domain")}
              />
              Block this sender's domain
            </label>
          </div>
          <DialogFooter>
            <Button
              disabled={selectedId == null || markSpam.isPending}
              onClick={() => selectedId != null && markSpam.mutate({ id: selectedId, scope: spamScope })}
            >
              {markSpam.isPending ? "Saving…" : "Confirm"}
            </Button>
            <Button variant="outline" onClick={() => setSpamOpen(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
